import { Font, StyleSheet } from "@react-pdf/renderer"

// Register brand fonts
// PT Sans (preferred for web/PDF; SIL OFL)
Font.register({
  family: "PT Sans",
  fonts: [
    { src: "/fonts/PT_Sans/PTSans-Regular.ttf" },
    { src: "/fonts/PT_Sans/PTSans-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/PT_Sans/PTSans-Italic.ttf", fontStyle: "italic" },
    { src: "/fonts/PT_Sans/PTSans-BoldItalic.ttf", fontWeight: "bold", fontStyle: "italic" },
  ],
})

// Myriad Pro (use only if licensed for embedding)
Font.register({
  family: "Myriad Pro",
  fonts: [
    { src: "/fonts/myriad-pro/MYRIADPRO-REGULAR.OTF" },
    { src: "/fonts/myriad-pro/MYRIADPRO-BOLD.OTF", fontWeight: "bold" },
    { src: "/fonts/myriad-pro/MYRIADPRO-BOLDIT.OTF", fontWeight: "bold", fontStyle: "italic" },
    { src: "/fonts/myriad-pro/MYRIADPRO-CONDIT.OTF", fontStyle: "italic" },
    { src: "/fonts/myriad-pro/MyriadPro-Light.otf", fontWeight: 300 as any },
  ],
})

// Professional color palette matching TREO branding
export const colors = {
  // Brand Web palette
  primary: "#063053", // Dark blue
  secondary: "#0666B0", // Blue
  text: "#000000", // Black for main text

  // Whites/Greys
  paper: "#FFFFFF", // True white for page background (paper)
  surface: "#F6F6F6", // Brand "white" (not pure white) for surfaces/fills
  lightGray: "#F6F6F6", // Alias to brand surface for compatibility
  tableHeader: "#F6F6F6", // Use brand surface for subtle table headers
  mediumGray: "#E6E6E6", // Brand grey for borders and dividers
  darkGray: "#666666", // Secondary text
  tableBorder: "#063053", // Use dark blue for emphasis borders

  // Status
  danger: "#CC0000", // Draft/warnings
  success: "#006600", // Pass/success
}

// Enhanced typography system
export const typography = {
  // Main title for cover page
  coverTitle: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center" as any,
    color: colors.primary,
    marginBottom: 10,
    letterSpacing: 1,
  },
  // Subtitle for cover page
  coverSubtitle: {
    fontSize: 18,
    textAlign: "center" as any,
    color: colors.text,
    marginBottom: 20,
  },
  // Section headers (H1)
  h1: {
    fontSize: 16,
    fontWeight: "bold",
    color: colors.primary,
    marginTop: 20,
    marginBottom: 12,
  },
  // Subsection headers (H2)
  h2: {
    fontSize: 14,
    fontWeight: "bold",
    color: colors.text,
    marginTop: 16,
    marginBottom: 10,
  },
  // Sub-subsection headers (H3)
  h3: {
    fontSize: 12,
    fontWeight: "bold",
    color: colors.text,
    marginTop: 12,
    marginBottom: 8,
  },
  // Normal body text
  body: {
    fontSize: 10,
    color: colors.text,
    lineHeight: 1.5,
  },
  // Small text for captions/footnotes
  caption: {
    fontSize: 9,
    color: colors.darkGray,
    fontStyle: "italic",
  },
  // Bold text
  bold: {
    fontWeight: "bold",
  },
  // Italic text
  italic: {
    fontStyle: "italic",
  },
}

// Professional table styles
export const tableStyles = StyleSheet.create({
  table: {
    width: "100%",
    marginVertical: 12,
    borderWidth: 1,
    borderColor: colors.tableBorder,
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    backgroundColor: colors.tableHeader,
    borderBottomWidth: 2,
    borderBottomColor: colors.tableBorder,
    borderTopWidth: 1,
    borderTopColor: colors.tableBorder,
  },
  tableHeaderCell: {
    flex: 1,
    padding: 10,
    fontSize: 10,
    fontWeight: "bold",
    color: colors.primary,
    borderRightWidth: 1,
    borderRightColor: colors.mediumGray,
    borderLeftWidth: 0.5,
    borderLeftColor: colors.mediumGray,
    textAlign: "center" as any,
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 9,
    color: colors.text,
    borderRightWidth: 0.5,
    borderRightColor: colors.mediumGray,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.mediumGray,
    borderLeftWidth: 0.5,
    borderLeftColor: colors.mediumGray,
  },
  tableCellBold: {
    fontWeight: "bold",
  },
  // Alternating row colors for better readability
  tableRowOdd: {
    backgroundColor: "#FFFFFF",
  },
  tableRowEven: {
    backgroundColor: colors.lightGray,
  },
  tableCaption: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center" as any,
    marginTop: 4,
    marginBottom: 8,
    color: colors.text,
  },
  // Special styles for specific table types
  resultTablePass: {
    color: colors.success,
    fontWeight: "bold",
  },
  resultTableFail: {
    color: colors.danger,
    fontWeight: "bold",
  },
})

// Cover page specific styles
export const coverPageStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 40,
  },
  logoSection: {
    alignItems: "center" as any,
    marginBottom: 15, // Reduced from 30
  },
  logoText: {
    fontSize: 28,
    fontWeight: "bold",
    color: colors.primary,
    letterSpacing: 2,
  },
  logoSubtext: {
    fontSize: 12,
    color: colors.darkGray,
    marginTop: 5,
  },
  titleSection: {
    marginVertical: 20, // Reduced from 40
    alignItems: "center" as any,
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.primary,
    textAlign: "center" as any,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 20,
    color: colors.text,
    textAlign: "center" as any,
  },
  divider: {
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
    marginVertical: 10, // Reduced from 20
  },
  infoSection: {
    marginVertical: 10, // Reduced from 15
  },
  infoTable: {
    marginVertical: 10,
  },
  infoRow: {
    flexDirection: "row",
    marginVertical: 4,
  },
  infoLabel: {
    flex: 1,
    fontSize: 11,
    fontWeight: "bold",
    color: colors.text,
  },
  infoValue: {
    flex: 2,
    fontSize: 11,
    color: colors.text,
  },
  signatureSection: {
    marginTop: 20, // Reduced from 40
    borderTopWidth: 1,
    borderTopColor: colors.mediumGray,
    paddingTop: 15, // Reduced from 20
  },
  signatureRow: {
    flexDirection: "row",
    marginVertical: 10, // Reduced from 15
  },
  signatureBlock: {
    flex: 1,
    paddingHorizontal: 10,
  },
  signatureLabel: {
    fontSize: 10,
    fontWeight: "bold",
    color: colors.text,
    marginBottom: 5,
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: colors.text,
    marginTop: 15, // Reduced from 25
    marginBottom: 5,
  },
  signatureName: {
    fontSize: 10,
    color: colors.text,
  },
  classificationBox: {
    position: "absolute" as any,
    top: 20,
    right: 20,
    borderWidth: 2,
    borderColor: "black",
    borderRadius: 3,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: "#FFF",
  },
  classificationText: {
    fontSize: 10,
    fontWeight: "bold",
    color: "black",
    textTransform: "uppercase",
  },
})

// TOC specific styles
export const tocStyles = StyleSheet.create({
  container: {
    marginVertical: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center" as any,
    color: colors.primary,
    marginBottom: 20,
  },
  entry: {
    flexDirection: "row",
    marginVertical: 4,
    alignItems: "flex-end" as any,
  },
  entryLevel1: {
    marginVertical: 6,
  },
  entryLevel2: {
    marginLeft: 20,
  },
  entryLevel3: {
    marginLeft: 40,
  },
  entryText: {
    flex: 1,
    fontSize: 11,
    color: colors.text,
  },
  entryTextBold: {
    fontWeight: "bold",
  },
  dottedLine: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomStyle: "dotted",
    borderBottomColor: colors.darkGray,
    marginHorizontal: 8,
    marginBottom: 2,
  },
  pageNumber: {
    fontSize: 11,
    color: colors.text,
    minWidth: 20,
    textAlign: "right" as any,
  },
})

// Header and footer styles
export const headerFooterStyles = StyleSheet.create({
  header: {
    position: "absolute" as any,
    top: 25,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center" as any,
    borderBottomWidth: 1,
    borderBottomColor: colors.mediumGray,
    paddingBottom: 5,
  },
  headerLeft: {
    fontSize: 10,
    color: colors.darkGray,
  },
  headerCenter: {
    fontSize: 10,
    fontWeight: "bold",
    color: colors.text,
  },
  headerRight: {
    padding: 5,
    borderWidth: 1,
    borderColor: colors.mediumGray,
  },
  headerRightText: {
    fontSize: 10,
    fontWeight: "bold",
    color: colors.text,
  },
  footer: {
    position: "absolute" as any,
    bottom: 25,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end" as any,
    borderTopWidth: 1,
    borderTopColor: colors.mediumGray,
    paddingTop: 5,
  },
  footerLeft: {
    flexDirection: "column",
  },
  footerText: {
    fontSize: 8,
    color: colors.darkGray,
  },
  footerCenter: {
    fontSize: 9,
    color: colors.text,
  },
  footerRight: {
    fontSize: 9,
    color: colors.text,
  },
  pageNumber: {
    fontWeight: "bold",
  },
})

// Main document styles
export const documentStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 30,
    paddingTop: 80, // Space for header
    paddingBottom: 60, // Space for footer
    paddingLeft: 60, // Wider left margin
    paddingRight: 50, // Standard right margin
    fontFamily: "Myriad Pro",
  },
  coverPage: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 40,
    fontFamily: "Myriad Pro",
  },
  content: {
    flex: 1,
  },
  section: {
    marginVertical: 10,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: 10,
    textAlign: "justify" as any,
  },
  list: {
    marginLeft: 20,
    marginVertical: 5,
  },
  listItem: {
    flexDirection: "row",
    marginVertical: 2,
  },
  listBullet: {
    width: 20,
    fontSize: 10,
  },
  listContent: {
    flex: 1,
    fontSize: 10,
  },
})

// Image styles
export const imageStyles = StyleSheet.create({
  container: {
    marginVertical: 15,
    alignItems: "center" as any,
  },
  image: {
    maxWidth: "100%",
    maxHeight: 400,
  },
  imageGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-around",
    gap: 10,
  },
  gridImage: {
    width: "45%",
    height: 200,
  },
  caption: {
    fontSize: 9,
    textAlign: "center" as any,
    marginTop: 8,
    color: colors.darkGray,
    fontStyle: "italic",
  },
  captionBold: {
    fontWeight: "bold",
    fontStyle: "normal",
  },
})

// Watermark styles
export const watermarkStyles = StyleSheet.create({
  watermark: {
    position: "absolute" as any,
    top: "40%",
    left: "20%",
    transform: "rotate(-45deg)" as any,
    opacity: 0.1,
  },
  watermarkText: {
    fontSize: 80,
    fontWeight: "bold",
    color: colors.danger,
    letterSpacing: 10,
  },
})
